import { useRef, useState } from 'react'
import { useAnexar, useAnexos, useExcluirAnexo } from '../../../api/queries/gmuds'
import { Botao } from '../../../components/Botao'
import { AvisoDeErro, Esqueleto, EstadoVazio } from '../../../components/Estados'
import { CampoDeSelecao } from '../../../components/campos'
import { TEXTOS } from '../../../lib/glossario'
import type { CategoriaDeAnexo } from '../../../types/gmud'
import { LinhaDoAnexo } from './LinhaDoAnexo'

const CATEGORIAS: { valor: CategoriaDeAnexo; rotulo: string }[] = [
  { valor: 'EVIDENCIA_DE_TESTE', rotulo: 'Comprovação de teste' },
  { valor: 'EVIDENCIA_DE_EXECUCAO', rotulo: 'Comprovação da execução' },
  { valor: 'DOCUMENTO', rotulo: 'Documento' },
]

/**
 * As comprovações da solicitação: anexar, listar, baixar e excluir.
 *
 * **A aba existe porque "validar e concluir" precisava se apoiar em alguma coisa.** Até aqui, a
 * comprovação do que foi feito era o texto do plano — escrito por quem executou. O servidor agora
 * recusa o encerramento sem ao menos uma comprovação de teste anexada, e esta tela é onde ela entra.
 *
 * **A categoria é escolhida antes do arquivo, e não depois.** É ela que decide se o anexo satisfaz
 * a exigência do encerramento; um seletor colocado depois do upload faria a pessoa anexar, ver a
 * recusa continuar de pé e não entender por quê.
 *
 * O aviso do que a exigência significa fica no topo, sempre visível — inclusive quando já há
 * comprovação. Quem chega aqui pela recusa do botão precisa ler o motivo sem procurar.
 *
 * @param props a solicitação
 * @returns a aba de comprovações
 */
export function Anexos({ gmudId }: { gmudId: number }) {
  const { data: anexos, isPending, isError, error } = useAnexos(gmudId)
  const anexar = useAnexar(gmudId)
  const excluir = useExcluirAnexo(gmudId)
  const [categoria, setCategoria] = useState<CategoriaDeAnexo>('EVIDENCIA_DE_TESTE')
  const campo = useRef<HTMLInputElement>(null)

  async function aoEscolher(arquivo: File | undefined) {
    if (!arquivo) {
      return
    }
    anexar.reset()
    try {
      await anexar.mutateAsync({ arquivo, categoria })
    } catch {
      // O AvisoDeErro abaixo diz o que o servidor recusou e por quê.
    } finally {
      // Limpa o campo mesmo em erro: sem isso, escolher o MESMO arquivo de novo não dispara
      // `change`, e a pessoa conclui que o botão parou de funcionar.
      if (campo.current) {
        campo.current.value = ''
      }
    }
  }

  return (
    <section className="space-y-4">
      <p className="max-w-prose rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-600">{TEXTOS.anexos.ajuda}</p>

      <div className="cartao space-y-3 p-4">
        <CampoDeSelecao
          rotulo="Para que serve este arquivo"
          ajuda={TEXTOS.anexos.categoriaAjuda}
          value={categoria}
          onChange={(e) => setCategoria(e.target.value as CategoriaDeAnexo)}
        >
          {CATEGORIAS.map((c) => (
            <option key={c.valor} value={c.valor}>
              {c.rotulo}
            </option>
          ))}
        </CampoDeSelecao>

        <input
          ref={campo}
          type="file"
          className="sr-only"
          onChange={(e) => void aoEscolher(e.target.files?.[0])}
          aria-label={TEXTOS.anexos.escolher}
        />
        <Botao aparencia="primario" onClick={() => campo.current?.click()} disabled={anexar.isPending}>
          {anexar.isPending ? TEXTOS.anexos.enviando : TEXTOS.anexos.escolher}
        </Botao>
        <p className="text-xs text-slate-500">{TEXTOS.anexos.formatos}</p>

        {anexar.isError && <AvisoDeErro erro={anexar.error} titulo={TEXTOS.anexos.naoAnexou} />}
      </div>

      {isPending && (
        <div className="cartao px-4 py-5">
          <Esqueleto linhas={3} />
        </div>
      )}
      {isError && <AvisoDeErro erro={error} titulo={TEXTOS.anexos.naoCarregou} />}

      {anexos && anexos.length === 0 && (
        <EstadoVazio titulo={TEXTOS.anexos.vazio.titulo} ajuda={TEXTOS.anexos.vazio.ajuda} />
      )}

      {anexos && anexos.length > 0 && (
        <ul className="cartao divide-y divide-slate-100">
          {anexos.map((anexo) => (
            <LinhaDoAnexo
              key={anexo.id}
              gmudId={gmudId}
              anexo={anexo}
              aoExcluir={() => excluir.mutate(anexo.id)}
              excluindo={excluir.isPending}
            />
          ))}
        </ul>
      )}

      {excluir.isError && <AvisoDeErro erro={excluir.error} titulo={TEXTOS.anexos.naoExcluiu} />}
    </section>
  )
}
